import { styled } from '@linaria/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionPullRequest } from '../../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkDarkest,
  inkLight,
  secondaryBase,
  secondaryLightest,
  skyDark,
  skyLight,
  skyLighter,
} from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { fmtRelative, turnDisplayNumber } from '../../shared/formatters';
import { prStateColor, prStateIcon } from '../../shared/pr-state.helpers';
import {
  Card,
  FileName,
  SectionTitle,
  sessionFallbackTitle,
  StatePill,
  SubtleText,
  ThisBadge,
} from './pr-detail.helpers';
import { buildLineage } from './pr-survival.helpers';

const LINEAGE_CAP = 8;

type PrLineageProps = {
  pr: SessionPullRequest;
  sessionId: string;
};

/**
 * "How the agent's code reached this PR": a three-column flow from agent edits,
 * through the files they landed in, to the merged pull request. Hovering an edit
 * or a file highlights what it is connected to. Renders nothing when the
 * payload carries no lineage.
 *
 * @param {PrLineageProps} props - The selected PR and the viewed session id
 * @returns {JSX.Element | null} The lineage card, or null without lineage data
 */
export function PrLineage({ pr, sessionId }: PrLineageProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const lineage = buildLineage(pr, sessionId);

  if (!lineage.available) return null;

  const visibleEdits = expanded ? lineage.edits : lineage.edits.slice(0, LINEAGE_CAP);
  const visibleFiles = expanded ? lineage.files : lineage.files.slice(0, LINEAGE_CAP);
  const hiddenCount =
    lineage.edits.length -
    Math.min(lineage.edits.length, LINEAGE_CAP) +
    (lineage.files.length - Math.min(lineage.files.length, LINEAGE_CAP));

  const linkedToHover = (id: string): boolean => {
    if (!hoveredId) return false;
    if (hoveredId === id) return true;
    return (
      (lineage.editToFiles.get(hoveredId) ?? []).includes(id) || (lineage.fileToEdits.get(hoveredId) ?? []).includes(id)
    );
  };

  const stateColor = prStateColor(pr.state);

  return (
    <Card>
      <div>
        <SectionTitle>{t('agent_miner.pull_requests.lineage.title')}</SectionTitle>
        <Subtitle>{t('agent_miner.pull_requests.lineage.subtitle')}</Subtitle>
      </div>
      <Grid>
        <Column aria-label={t('agent_miner.pull_requests.lineage.col_edits')}>
          <ColumnHeading>{t('agent_miner.pull_requests.lineage.col_edits')}</ColumnHeading>
          {visibleEdits.map((edit) => (
            <Node
              key={edit.file_edit_id}
              $highlight={linkedToHover(edit.file_edit_id)}
              onMouseEnter={() => setHoveredId(edit.file_edit_id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <NodeTop>
                <TurnChip>
                  {t('agent_miner.pull_requests.lineage.turn', { turn: turnDisplayNumber(edit.turn_number ?? 0) })}
                </TurnChip>
                {edit.is_current ? (
                  <ThisBadge>{t('agent_miner.pull_requests.lineage.this_session')}</ThisBadge>
                ) : (
                  <NodeTitle title={edit.session_title ?? ''}>
                    {edit.session_title ?? sessionFallbackTitle(edit.session_id)}
                  </NodeTitle>
                )}
              </NodeTop>
              {edit.lines_matched !== null && (
                <NodeMeta>
                  {t('agent_miner.pull_requests.lineage.lines_matched', { count: edit.lines_matched })}
                </NodeMeta>
              )}
            </Node>
          ))}
        </Column>
        <ArrowColumn aria-hidden="true">
          <Icon name="arrow_forward" size={16} color={skyDark} />
        </ArrowColumn>
        <Column aria-label={t('agent_miner.pull_requests.lineage.col_files')}>
          <ColumnHeading>{t('agent_miner.pull_requests.lineage.col_files')}</ColumnHeading>
          {visibleFiles.map((file) => (
            <Node
              key={file.change_request_file_id}
              $highlight={linkedToHover(file.change_request_file_id)}
              onMouseEnter={() => setHoveredId(file.change_request_file_id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <FileName title={file.filename ?? ''}>{file.filename}</FileName>
              {file.lines_matched !== null && (
                <NodeMeta>
                  {t('agent_miner.pull_requests.lineage.lines_matched', { count: file.lines_matched })}
                </NodeMeta>
              )}
            </Node>
          ))}
        </Column>
        <ArrowColumn aria-hidden="true">
          <Icon name="arrow_forward" size={16} color={skyDark} />
        </ArrowColumn>
        <Column aria-label={t('agent_miner.pull_requests.lineage.col_pr')}>
          <ColumnHeading>{t('agent_miner.pull_requests.lineage.col_pr')}</ColumnHeading>
          <Node $highlight={false}>
            <NodeTop>
              <NodeTitle title={pr.title ?? ''}>
                {pr.number != null ? `#${pr.number}` : 'PR'} {pr.title}
              </NodeTitle>
            </NodeTop>
            <NodeTop>
              <StatePillColored $color={stateColor}>
                <Icon name={prStateIcon(pr.state)} size={13} color={stateColor} />
                {pr.state}
              </StatePillColored>
              <NodeMeta>
                {pr.merged_at
                  ? t('agent_miner.pull_requests.lineage.merged', { ago: fmtRelative(pr.merged_at) })
                  : t('agent_miner.pull_requests.lineage.not_merged')}
              </NodeMeta>
            </NodeTop>
          </Node>
        </Column>
      </Grid>
      {hiddenCount > 0 && (
        <ShowMore type="button" aria-expanded={expanded} onClick={() => setExpanded((prev) => !prev)}>
          {expanded
            ? t('agent_miner.pull_requests.lineage.show_less')
            : t('agent_miner.pull_requests.lineage.show_more', { count: hiddenCount })}
        </ShowMore>
      )}
    </Card>
  );
}

const StatePillColored = styled(StatePill)<{ $color: string }>`
  color: ${(props) => props.$color};
  border-color: ${(props) => props.$color};
`;

const Subtitle = styled(SubtleText)`
  display: block;
  margin-top: 6px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px minmax(0, 1fr) 28px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
`;

const Column = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const ColumnHeading = styled.li`
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-bottom: 2px;
`;

const ArrowColumn = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 28px;
`;

const Node = styled.li<{ $highlight: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$highlight ? secondaryBase : skyLight)};
  background: ${(props) => (props.$highlight ? secondaryLightest : skyLighter)};
  min-width: 0;
  transition: border-color 0.12s ease, background 0.12s ease;
`;

const NodeTop = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

const NodeTitle = styled.span`
  font: 600 12px/1.3 'Figtree', sans-serif;
  color: ${inkDarkest};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TurnChip = styled.span`
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 99px;
  background: ${secondaryLightest};
  color: ${secondaryBase};
  font: 600 10px/1.3 'Figtree', sans-serif;
`;

const NodeMeta = styled.span`
  font: 500 10.5px/1.2 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const ShowMore = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  color: ${secondaryBase};
  font: 600 12px/1 'Figtree', sans-serif;
  cursor: pointer;
`;
